import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import b2bService from '../../services/b2bService';
import B2bBulkBookingPanel from './B2bBulkBookingPanel';
import './B2bPortalBookOrderPage.css';

export default function B2bPortalBookOrderPage() {
  const navigate = useNavigate();

  // Retrieve client portal user details from sessionStorage (Zero-Trust Session Isolation)
  const portalUserData = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('b2b_portal_user') || '{}');
    } catch {
      return {};
    }
  })();

  const isViewer = /viewer/i.test(portalUserData.role || '');
  const isBillingOnly = /billing/i.test(portalUserData.role || '');
  const isRegistrationBlocked = portalUserData.allowPortalRegistration === false || isViewer || isBillingOnly;

  // Active Tab State: 'single' | 'bulk'
  const [activeTab, setActiveTab] = useState('single');

  // Patient Demographics State
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [title, setTitle] = useState('Mr.');
  const [patientName, setPatientName] = useState('');
  const [gender, setGender] = useState('Male');
  const [ageYears, setAgeYears] = useState('');
  const [ageMonths, setAgeMonths] = useState('');
  const [ageDays, setAgeDays] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [clientOrderNumber, setClientOrderNumber] = useState('');

  // Scoped Lookup State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Test Catalog & Cart State
  const [availableTests, setAvailableTests] = useState([]);
  const [testSearch, setTestSearch] = useState('');
  const [loadingTests, setLoadingTests] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);

  // Submission & Dialog State
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successOrder, setSuccessOrder] = useState(null);

  // Load available tests for this party contract
  const loadTests = useCallback(async (query = '') => {
    try {
      setLoadingTests(true);
      const tests = await b2bService.getPortalAvailableTests(query);
      setAvailableTests(tests || []);
    } catch (err) {
      console.error('Failed to load portal tests:', err);
    } finally {
      setLoadingTests(false);
    }
  }, []);

  useEffect(() => {
    if (!isRegistrationBlocked) {
      loadTests('');
    }
  }, [loadTests, isRegistrationBlocked]);

  // Handle patient autocomplete search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!val || val.trim().length < 3) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearchingPatients(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await b2bService.searchPortalPatients(val);
        setSearchResults(results || []);
        setShowSearchResults(true);
      } catch (err) {
        console.error('Scoped patient search error:', err);
      } finally {
        setSearchingPatients(false);
      }
    }, 300);
  };

  // Select an existing patient from scoped history
  const handleSelectPatient = (p) => {
    setSelectedPatientId(p.patientId);
    setTitle(p.title || 'Mr.');
    setPatientName(p.pName || '');
    setGender(p.gender || 'Male');
    setAgeYears(p.ageYear ? String(p.ageYear) : '');
    setAgeMonths(p.ageMonth ? String(p.ageMonth) : '');
    setAgeDays(p.ageDays ? String(p.ageDays) : '');
    setMobile(p.mobile || '');
    setEmail(p.email || '');
    setAddress(p.address || '');
    setShowSearchResults(false);
    setSearchQuery('');
  };

  // Reset to create a brand new patient
  const handleClearPatient = () => {
    setSelectedPatientId(null);
    setTitle('Mr.');
    setPatientName('');
    setGender('Male');
    setAgeYears('');
    setAgeMonths('');
    setAgeDays('');
    setMobile('');
    setEmail('');
    setAddress('');
    setDoctorName('');
    setClientOrderNumber('');
    setSearchQuery('');
  };

  // Test Selection
  const handleAddTest = (test) => {
    if (selectedTests.some((t) => t.investigationId === test.investigationId)) return;
    setSelectedTests([...selectedTests, test]);
  };

  const handleRemoveTest = (investigationId) => {
    setSelectedTests(selectedTests.filter((t) => t.investigationId !== investigationId));
  };

  const netTotal = selectedTests.reduce((acc, t) => acc + (Number(t.rate) || 0), 0);

  // Submit Order Booking
  const handleBookOrder = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!patientName.trim()) {
      setSubmitError('Patient Name is mandatory.');
      return;
    }
    if (!gender) {
      setSubmitError('Please specify Gender.');
      return;
    }
    if (selectedTests.length === 0) {
      setSubmitError('Please select at least one laboratory investigation.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        patientId: selectedPatientId,
        title,
        patientName: patientName.trim(),
        gender,
        ageYears: ageYears ? parseInt(ageYears, 10) : 0,
        ageMonths: ageMonths ? parseInt(ageMonths, 10) : 0,
        ageDays: ageDays ? parseInt(ageDays, 10) : 0,
        mobile: mobile.trim(),
        email: email.trim() || null,
        address: address.trim() || null,
        doctorName: doctorName.trim() || 'Self / B2B Referral',
        clientOrderNumber: clientOrderNumber.trim() || null,
        investigationIds: selectedTests.map((t) => t.investigationId)
      };

      const result = await b2bService.bookPortalOrder(payload);
      setSuccessOrder(result);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to book order. Please try again.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintSlip = () => {
    window.print();
  };

  const handleResetForNextOrder = () => {
    setSuccessOrder(null);
    handleClearPatient();
    setSelectedTests([]);
    setSubmitError(null);
  };

  // Access check: If portal registration is turned off by Admin or user is VIEWER
  if (isRegistrationBlocked) {
    return (
      <div className="b2b-book-container">
        <div className="b2b-error-alert" style={{ margin: '40px auto', maxWidth: '600px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>Registration Privileges Restricted</h3>
          <p style={{ margin: 0 }}>
            {isViewer
              ? 'Your account is configured with Read-Only (Viewer) access. Direct patient booking is disabled.'
              : 'Online patient registration is currently disabled for your facility. Please contact the laboratory administration to enable booking permissions.'}
          </p>
        </div>
      </div>
    );
  }

  const filteredTests = availableTests.filter((t) => {
    if (!testSearch.trim()) return true;
    const q = testSearch.toLowerCase();
    return (t.testName && t.testName.toLowerCase().includes(q)) ||
      (t.testCode && t.testCode.toLowerCase().includes(q)) ||
      (t.department && t.department.toLowerCase().includes(q));
  });

  return (
    <div className="b2b-book-container">
      {/* Header Banner */}
      <div className="b2b-book-header">
        <div className="b2b-book-title-area">
          <h2>
            <span>Patient Registration & Order Booking</span>
          </h2>
          <p className="b2b-book-subtitle">
            Facility: <strong>{portalUserData.partyName}</strong> ({portalUserData.partyCode}) &bull; Fast-track clinical intake
          </p>
        </div>
        <div className="b2b-security-badge" title="Zero cross-party data leakage guarantee">
          <span className="b2b-security-badge-icon">&#128274;</span>
          <span>Tenant Isolated: Scoped Patient History Only</span>
        </div>
      </div>

      {/* Mode Navigation Tabs: Single vs Bulk Excel */}
      <div style={{ display: 'flex', gap: '12px', borderBottom: '2px solid #e2e8f0', margin: '4px 0 10px 0' }}>
        <button
          type="button"
          onClick={() => setActiveTab('single')}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 700,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            borderBottom: activeTab === 'single' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'single' ? '#0284c7' : '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>👤</span> Single Patient Registration
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('bulk')}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 700,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            borderBottom: activeTab === 'bulk' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'bulk' ? '#0284c7' : '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>📁</span> Excel / CSV Bulk Booking (Corporate Camps)
        </button>
      </div>

      {activeTab === 'bulk' ? (
        <B2bBulkBookingPanel
          availableTests={availableTests}
          partyCode={portalUserData.partyCode}
          partyName={portalUserData.partyName}
        />
      ) : (
        <>
          {submitError && (
            <div className="b2b-error-alert" role="alert">
              <strong>Registration Failed:</strong> {submitError}
            </div>
          )}

          {/* Main Two-Column Layout */}
          <form onSubmit={handleBookOrder} className="b2b-book-grid">
        {/* Column 1: Patient Information & Scoped Lookup */}
        <div className="b2b-book-card">
          <div className="b2b-card-title">
            <span>1. Patient Demographics</span>
            {selectedPatientId && (
              <span style={{ fontSize: '12px', color: '#047857', fontWeight: 600 }}>
                Existing Patient Mode
              </span>
            )}
          </div>

          {/* Scoped Patient Lookup Input */}
          <div className="b2b-lookup-section">
            <label className="b2b-lookup-label">
              Find Past Patient (Search by Mobile, UHID, or Name)
            </label>
            <div className="b2b-lookup-input-wrapper">
              <input
                type="text"
                className="b2b-lookup-input"
                placeholder="Type 3+ characters (e.g. 9876543210 or John)..."
                value={searchQuery}
                onChange={handleSearchChange}
                disabled={Boolean(selectedPatientId)}
              />
              {searchingPatients && <span className="b2b-lookup-spinner">&#8987;</span>}
            </div>

            {/* Suggestions dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="b2b-lookup-results">
                {searchResults.map((p) => (
                  <div
                    key={p.patientId}
                    className="b2b-lookup-item"
                    onClick={() => handleSelectPatient(p)}
                  >
                    <div className="b2b-lookup-item-header">
                      <span>{p.title} {p.pName}</span>
                      <span style={{ color: '#2563eb' }}>{p.patientId}</span>
                    </div>
                    <div className="b2b-lookup-item-sub">
                      {p.gender} &bull; {p.ageYear ? `${p.ageYear} Yrs` : 'Age N/A'} &bull; Phone: {p.mobile || 'None'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showSearchResults && searchResults.length === 0 && searchQuery.length >= 3 && !searchingPatients && (
              <div className="b2b-lookup-results" style={{ padding: '10px 14px', color: '#64748b', fontSize: '12px' }}>
                No prior patients found under your facility matching &ldquo;{searchQuery}&rdquo;. Fill details below to register a new patient.
              </div>
            )}

            {selectedPatientId && (
              <div className="b2b-active-patient-pill">
                <span>
                  Linked to Scoped Record: <strong>{selectedPatientId}</strong> ({patientName})
                </span>
                <button type="button" onClick={handleClearPatient} title="Clear and create a new patient">
                  &times; Clear &amp; New Patient
                </button>
              </div>
            )}
          </div>

          {/* Form Demographics Fields */}
          <div className="b2b-form-row-3">
            <div className="b2b-form-group">
              <label className="b2b-form-label">Title</label>
              <select
                className="b2b-form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              >
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Miss">Miss</option>
                <option value="Master">Master</option>
                <option value="Dr.">Dr.</option>
                <option value="Baby">Baby</option>
              </select>
            </div>
            <div className="b2b-form-group" style={{ gridColumn: 'span 2' }}>
              <label className="b2b-form-label">
                Patient Name <span className="b2b-required">*</span>
              </label>
              <input
                type="text"
                className="b2b-form-control"
                placeholder="Full Legal Name"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="b2b-form-row">
            <div className="b2b-form-group">
              <label className="b2b-form-label">
                Gender <span className="b2b-required">*</span>
              </label>
              <select
                className="b2b-form-control"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                required
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="b2b-form-group">
              <label className="b2b-form-label">Age</label>
              <div className="b2b-age-inputs">
                <div className="b2b-age-field">
                  <input
                    type="number"
                    min="0"
                    max="130"
                    className="b2b-form-control"
                    placeholder="0"
                    value={ageYears}
                    onChange={(e) => setAgeYears(e.target.value)}
                  />
                  <span className="b2b-age-sublabel">Yrs</span>
                </div>
                <div className="b2b-age-field">
                  <input
                    type="number"
                    min="0"
                    max="11"
                    className="b2b-form-control"
                    placeholder="0"
                    value={ageMonths}
                    onChange={(e) => setAgeMonths(e.target.value)}
                  />
                  <span className="b2b-age-sublabel">Mos</span>
                </div>
                <div className="b2b-age-field">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    className="b2b-form-control"
                    placeholder="0"
                    value={ageDays}
                    onChange={(e) => setAgeDays(e.target.value)}
                  />
                  <span className="b2b-age-sublabel">Days</span>
                </div>
              </div>
            </div>
          </div>

          <div className="b2b-form-row">
            <div className="b2b-form-group">
              <label className="b2b-form-label">Mobile Number</label>
              <input
                type="tel"
                maxLength="15"
                className="b2b-form-control"
                placeholder="10-digit mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </div>
            <div className="b2b-form-group">
              <label className="b2b-form-label">Email Address</label>
              <input
                type="email"
                className="b2b-form-control"
                placeholder="Optional report delivery email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="b2b-form-group">
            <label className="b2b-form-label">Residential Address</label>
            <input
              type="text"
              className="b2b-form-control"
              placeholder="City, Area or Street"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="b2b-form-row">
            <div className="b2b-form-group">
              <label className="b2b-form-label">Referring Doctor / Clinic</label>
              <input
                type="text"
                className="b2b-form-control"
                placeholder="Self / B2B Referral"
                value={doctorName}
                onChange={(e) => setDoctorName(e.target.value)}
              />
            </div>
            <div className="b2b-form-group">
              <label className="b2b-form-label">Client Ref / Sample Barcode</label>
              <input
                type="text"
                className="b2b-form-control"
                placeholder="Optional external tracking ID"
                value={clientOrderNumber}
                onChange={(e) => setClientOrderNumber(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Column 2: Test Selection & Order Summary */}
        <div className="b2b-book-card">
          <div className="b2b-card-title">
            <span>2. Test Selection &amp; Tariff</span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              {selectedTests.length} item(s) selected
            </span>
          </div>

          {/* Test Search Field */}
          <div className="b2b-test-search-box">
            <input
              type="text"
              className="b2b-form-control"
              placeholder="Filter test catalogue by name, code or department..."
              value={testSearch}
              onChange={(e) => setTestSearch(e.target.value)}
            />
          </div>

          {/* Available Tests List */}
          <div className="b2b-test-list">
            {loadingTests ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                Loading available investigations...
              </div>
            ) : filteredTests.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                No active tests match your search.
              </div>
            ) : (
              filteredTests.map((t) => {
                const isSelected = selectedTests.some((st) => st.investigationId === t.investigationId);
                return (
                  <div key={t.investigationId} className="b2b-test-item">
                    <div className="b2b-test-item-info">
                      <span className="b2b-test-item-name">{t.testName}</span>
                      <span className="b2b-test-item-code">
                        Code: {t.testCode} &bull; {t.department || 'Pathology'}
                      </span>
                    </div>
                    <div className="b2b-test-item-action">
                      <span className="b2b-test-price">
                        &#8377; {Number(t.rate || 0).toFixed(2)}
                      </span>
                      <button
                        type="button"
                        className="b2b-btn-add-test"
                        onClick={() => handleAddTest(t)}
                        disabled={isSelected}
                      >
                        {isSelected ? 'Added' : '+ Add'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Selected Test Cart */}
          <div className="b2b-cart-container">
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>
              Selected Investigations ({selectedTests.length})
            </div>

            {selectedTests.length === 0 ? (
              <div className="b2b-cart-empty">
                No tests added yet. Search and click &ldquo;+ Add&rdquo; above.
              </div>
            ) : (
              <table className="b2b-cart-table">
                <thead>
                  <tr>
                    <th>Test</th>
                    <th>Code</th>
                    <th style={{ textAlign: 'right' }}>Contract Rate</th>
                    <th style={{ width: '30px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTests.map((t) => (
                    <tr key={t.investigationId}>
                      <td><strong>{t.testName}</strong></td>
                      <td style={{ color: '#64748b' }}>{t.testCode}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        &#8377; {Number(t.rate || 0).toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="b2b-btn-remove-test"
                          onClick={() => handleRemoveTest(t.investigationId)}
                          title="Remove test"
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="b2b-cart-footer">
              <span className="b2b-cart-count">{selectedTests.length} Investigation(s)</span>
              <span className="b2b-cart-total">
                Net Total: &#8377; {netTotal.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Submit Action */}
          <div className="b2b-submit-bar">
            <button
              type="submit"
              className="b2b-btn-book"
              disabled={submitting || selectedTests.length === 0}
            >
              {submitting ? 'Registering & Booking...' : 'Confirm & Book Order'}
            </button>
          </div>
        </div>
      </form>

      {/* Success Modal / Print Accession Dialog */}
      {successOrder && (
        <div className="b2b-success-overlay">
          <div className="b2b-success-dialog">
            <div className="b2b-success-icon">&#10003;</div>
            <h3 className="b2b-success-title">Order Successfully Accessioned!</h3>

            <div className="b2b-success-summary-box">
              <div className="b2b-success-row">
                <span className="b2b-success-key">Lab Order / Accession #</span>
                <span className="b2b-success-val" style={{ color: '#2563eb' }}>
                  {successOrder.ledgerTransactionNo}
                </span>
              </div>
              <div className="b2b-success-row">
                <span className="b2b-success-key">Patient UHID</span>
                <span className="b2b-success-val">{successOrder.patientId}</span>
              </div>
              <div className="b2b-success-row">
                <span className="b2b-success-key">Patient Name</span>
                <span className="b2b-success-val">{successOrder.patientName}</span>
              </div>
              <div className="b2b-success-row">
                <span className="b2b-success-key">Tests Booked</span>
                <span className="b2b-success-val">{successOrder.testCount} Test(s)</span>
              </div>
              <div className="b2b-success-row" style={{ gridColumn: 'span 2' }}>
                <span className="b2b-success-key">Total Order Value</span>
                <span className="b2b-success-val" style={{ color: '#047857', fontSize: '16px' }}>
                  &#8377; {Number(successOrder.netAmount || 0).toFixed(2)}
                </span>
              </div>
            </div>

            <div style={{ fontSize: '12px', color: '#475569', textAlign: 'center' }}>
              Your order is now queued for specimen accessioning and processing. You can monitor test progress directly from the Orders &amp; Reports dashboard.
            </div>

            <div className="b2b-success-actions">
              <button
                type="button"
                className="b2b-btn-secondary"
                onClick={handlePrintSlip}
              >
                &#128438; Print Slip
              </button>
              <button
                type="button"
                className="b2b-btn-primary"
                onClick={handleResetForNextOrder}
              >
                + Book Another Patient
              </button>
              <button
                type="button"
                className="b2b-btn-secondary"
                onClick={() => navigate('/b2b/portal/orders')}
              >
                View Orders &rarr;
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

